import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import type { AssistantRecentError } from '../utils/assistantErrorBuffer';

export const ASSISTANT_MESSAGE_MAX_LENGTH = 4000;

/** Nome exibido no menu, widget e telas (rota permanece `/central-ia`). */
export const SUPPORT_CENTER_DISPLAY_NAME = 'Central de suporte';

export interface AssistantSessionContext {
  user_id: string;
  user_name: string;
  user_email_masked: string | null;
  role: string;
  role_label: string;
  is_unit_manager: boolean;
  unit_id: string | null;
  unit_name: string | null;
  unit_selected: boolean;
  available_units: Array<{ id: string; name: string }>;
  professional_id: string | null;
  professional_name: string | null;
  impersonated_by: { id: string; name: string } | null;
  data_tools_enabled: boolean;
  actions_enabled: boolean;
}

export interface AssistantStatus {
  enabled: boolean;
  tools_enabled: boolean;
  actions_enabled: boolean;
  data_opt_in: boolean;
  actions_opt_in?: boolean;
  server_tools_enabled?: boolean;
  server_actions_enabled?: boolean;
  vision_enabled?: boolean;
  max_images_per_message?: number;
  can_manage_settings?: boolean;
  session?: AssistantSessionContext;
}

export interface AssistantSettingsUpdate {
  data_tools_enabled?: boolean;
  actions_enabled?: boolean;
}

export interface AssistantClientContext {
  pathname?: string;
  pageTitle?: string;
  recentErrors?: AssistantRecentError[];
  appVersion?: string;
}

export interface AssistantChatRequest {
  conversation_id?: string;
  message: string;
  clientContext?: AssistantClientContext;
  images?: File[];
}

export interface AssistantMessageAttachment {
  mime: string;
  preview_url: string | null;
  bytes?: number;
  width?: number;
  height?: number;
}

export type ToolCallStatus = 'ok' | 'error' | 'denied';

export interface ToolCallSummary {
  tool: string;
  label: string;
  summary: string;
  status: ToolCallStatus;
}

export type ActionRisk = 'N2' | 'N3';
export type ActionReversibility = 'sim' | 'parcial' | 'nao';
export type ActionProposalStatus = 'proposed' | 'executed' | 'failed' | 'expired' | 'cancelled';

export interface ActionPreviewLine {
  label: string;
  value: string;
}

export interface ActionProposal {
  id: string;
  action: string;
  title: string;
  description: string;
  risk: ActionRisk;
  preview: {
    lines: ActionPreviewLine[];
    warnings: string[];
  };
  requires_reason: boolean;
  confirm_value?: string | null;
  reversible: ActionReversibility;
  status: ActionProposalStatus;
  expires_at: string;
}

export interface AssistantLink {
  label: string;
  href: string;
}

export type HumanSupportTicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved';

export interface HumanSupportState {
  protocol: string;
  status: HumanSupportTicketStatus;
  /** Chat com a equipe ativo neste servidor (hub configurado). */
  human_mode: boolean;
  last_seq: number;
}

export type AssistantMessageSender = 'user' | 'ai' | 'agent' | 'system';

export interface HumanSupportMessage {
  id: string;
  role: 'user' | 'assistant';
  sender: 'user' | 'agent' | 'system';
  content: string;
  author_name: string | null;
  hub_seq: number | null;
  delivery: 'pending' | 'sent' | 'failed' | null;
  attachments?: AssistantMessageAttachment[];
  created_at: string;
}

export interface AssistantChatResponse {
  conversation_id: string;
  message_id: string;
  reply: string;
  tool_calls: ToolCallSummary[];
  proposals: ActionProposal[];
  links: AssistantLink[];
  can_escalate: boolean;
  /** A própria IA encaminhou a conversa para a equipe nesta resposta. */
  escalation?: AssistantEscalateResponse;
  /** `human` quando a conversa já está com a equipe — sem resposta da IA. */
  mode?: 'ai' | 'human';
  human_message?: HumanSupportMessage;
  support?: HumanSupportState;
}

export interface ConfirmActionRequest {
  reason?: string;
  typed_value?: string;
}

export interface ConfirmActionResponse {
  proposal: ActionProposal;
  message: string;
}

export interface CancelActionResponse {
  proposal: ActionProposal;
}

export interface AssistantConversationSummary {
  id: string;
  title: string;
  status: string;
  created_at: string;
  last_message_at: string;
  last_message_preview?: string | null;
  last_message_role?: 'user' | 'assistant' | null;
}

export interface HelpArticleSummary {
  slug: string;
  title: string;
  excerpt: string;
  collection_id: string;
  collection_title: string;
}

export interface HelpArticleDetail extends HelpArticleSummary {
  body: string;
  related: HelpArticleSummary[];
}

export interface HelpCollectionSummary {
  id: string;
  title: string;
  description: string;
  article_count: number;
}

export interface HelpNewsItem {
  id: string;
  title: string;
  summary: string;
  date: string;
  tag: string;
  body: string;
  /** Rota interna do sistema (notas locais). */
  href: string | null;
  /** Post completo no blog público. */
  url: string | null;
  cover_url: string | null;
  read_minutes: number | null;
}

export const KLINVIA_BLOG_URL = 'https://klinvia.com/blog';

export interface HelpHome {
  suggested: HelpArticleSummary[];
  collections_count: number;
  latest_news: HelpNewsItem | null;
}

export interface AssistantConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  sender?: AssistantMessageSender;
  author_name?: string | null;
  hub_seq?: number | null;
  delivery?: 'pending' | 'sent' | 'failed' | null;
  content: string;
  created_at: string;
  tool_calls?: ToolCallSummary[];
  proposals?: ActionProposal[];
  attachments?: AssistantMessageAttachment[];
}

export interface AssistantConversationDetail {
  conversation: AssistantConversationSummary;
  support?: HumanSupportState | null;
  messages: AssistantConversationMessage[];
}

export interface HumanSupportThread {
  conversation_id: string;
  protocol: string;
  status: HumanSupportTicketStatus;
  last_agent_seq: number;
  last_agent_at: string | null;
}

export interface AssistantFeedbackRequest {
  conversation_id: string;
  message_id?: string;
  helpful: boolean;
  comment?: string;
}

export interface AssistantEscalateRequest {
  conversation_id: string;
  note?: string;
}

export interface AssistantEscalateResponse {
  protocol: string;
  message: string;
  hub_synced?: boolean;
  human_mode?: boolean;
}

export interface AssistantAdminMetrics {
  conversations: number;
  resolved: number;
  escalated: number;
  helpful: number;
  not_helpful: number;
  tool_calls: number;
  actions_executed: number;
  actions_failed: number;
  tokens_in: number;
  tokens_out: number;
  top_tools: Array<{ tool: string; count: number }>;
  top_pages: Array<{ pathname: string; count: number }>;
  top_actions?: Array<{ action: string; count: number }>;
  recent_escalations: Array<{ protocol: string; summary: string; created_at: string; status: string }>;
  unanswered: Array<{ id?: string; question: string; created_at: string }>;
}

export type AssistantErrorCode =
  | 'PROPOSAL_EXPIRED'
  | 'PROPOSAL_NOT_FOUND'
  | 'PROPOSAL_CHANGED'
  | 'REASON_REQUIRED'
  | 'VALUE_MISMATCH'
  | 'FORBIDDEN'
  | 'ACTIONS_DISABLED'
  | 'MIGRATION_REQUIRED';

export class AssistantApiError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'AssistantApiError';
    this.code = code;
    this.status = status;
  }
}

const ERROR_MESSAGES: Record<AssistantErrorCode, string> = {
  PROPOSAL_EXPIRED: 'Esta proposta expirou. Peça novamente à assistente para gerar uma nova prévia.',
  PROPOSAL_NOT_FOUND: 'Não encontramos esta proposta. Ela pode ter sido cancelada ou já executada.',
  PROPOSAL_CHANGED: 'Os dados mudaram desde a prévia. Peça à assistente uma nova proposta antes de confirmar.',
  REASON_REQUIRED: 'Informe o motivo para continuar.',
  VALUE_MISMATCH: 'O valor digitado não confere. Digite exatamente o texto indicado.',
  FORBIDDEN: 'Seu perfil não tem permissão para esta ação.',
  ACTIONS_DISABLED: 'As ações pela assistente estão desativadas nesta conta.',
  MIGRATION_REQUIRED: 'O banco desta clínica ainda não recebeu a atualização da Central de suporte. Fale com o suporte Klinvia.',
};

export function friendlyAssistantError(error: unknown, fallback = 'Não foi possível concluir agora. Tente novamente.'): string {
  if (error instanceof AssistantApiError) {
    if (error.code && error.code in ERROR_MESSAGES) {
      return ERROR_MESSAGES[error.code as AssistantErrorCode];
    }
    if (error.status === 429) return 'Muitas perguntas em pouco tempo. Aguarde alguns instantes e tente novamente.';
    if (error.status && error.status >= 500) return 'A assistente está indisponível no momento. Tente novamente em instantes.';
    return error.message || fallback;
  }
  return fallback;
}

function toAssistantError(error: any): AssistantApiError {
  if (error instanceof AssistantApiError) return error;
  const data = error?.response?.data;
  const message =
    (data && typeof data === 'object' && (data.error || data.message)) ||
    error?.message ||
    'Erro ao contactar a assistente.';
  return new AssistantApiError(String(message), data?.code, error?.response?.status);
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

function unwrap<T>(body: ApiEnvelope<T> | undefined): T {
  if (!body || body.success === false) {
    throw new AssistantApiError(body?.error || 'Resposta inválida da assistente.', body?.code);
  }
  return body.data as T;
}

const url = (path: string) => getApiUrl(`/assistant${path}`);

async function request<T>(fn: () => Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  try {
    const res = await fn();
    return unwrap(res.data);
  } catch (e) {
    throw toAssistantError(e);
  }
}

export function getAssistantStatus(): Promise<AssistantStatus> {
  return request(() => axios.get(url('/status')));
}

export function updateAssistantSettings(
  body: AssistantSettingsUpdate,
): Promise<{ data_opt_in: boolean; actions_opt_in: boolean }> {
  return request(() => axios.put(url('/settings'), body));
}

export function sendAssistantMessage(body: AssistantChatRequest): Promise<AssistantChatResponse> {
  const images = body.images?.filter(Boolean) || [];
  if (images.length) {
    const form = new FormData();
    if (body.conversation_id) form.append('conversation_id', body.conversation_id);
    form.append('message', body.message || '');
    if (body.clientContext) form.append('clientContext', JSON.stringify(body.clientContext));
    for (const file of images) form.append('images', file, file.name);
    return request(() =>
      axios.post(url('/chat'), form, {
        timeout: 120000,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  }
  return request(() => axios.post(url('/chat'), body, { timeout: 90000 }));
}

export function confirmAssistantAction(proposalId: string, body: ConfirmActionRequest): Promise<ConfirmActionResponse> {
  return request(() => axios.post(url(`/actions/${encodeURIComponent(proposalId)}/confirm`), body));
}

export function cancelAssistantAction(proposalId: string): Promise<CancelActionResponse> {
  return request(() => axios.post(url(`/actions/${encodeURIComponent(proposalId)}/cancel`), {}));
}

export function listAssistantConversations(): Promise<AssistantConversationSummary[]> {
  return request(() => axios.get(url('/conversations')));
}

export function getAssistantConversation(conversationId: string): Promise<AssistantConversationDetail> {
  return request(() => axios.get(url(`/conversations/${encodeURIComponent(conversationId)}`)));
}

export function sendAssistantFeedback(body: AssistantFeedbackRequest): Promise<void> {
  return request(() => axios.post(url('/feedback'), body)).then(() => undefined);
}

export function escalateAssistantConversation(body: AssistantEscalateRequest): Promise<AssistantEscalateResponse> {
  return request(() => axios.post(url('/escalate'), body));
}

export function sendHumanSupportMessage(
  conversationId: string,
  message: string,
  images: File[] = [],
): Promise<{ message: HumanSupportMessage; support: HumanSupportState }> {
  const files = images.filter(Boolean);
  if (files.length) {
    const form = new FormData();
    form.append('message', message || '');
    for (const file of files) form.append('images', file, file.name);
    return request(() =>
      axios.post(url(`/conversations/${encodeURIComponent(conversationId)}/human-messages`), form, {
        timeout: 120000,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  }
  return request(() =>
    axios.post(url(`/conversations/${encodeURIComponent(conversationId)}/human-messages`), { message }, { timeout: 30000 }),
  );
}

export function retryHumanSupportMessage(conversationId: string, messageId: string): Promise<{ message: HumanSupportMessage }> {
  return request(() =>
    axios.post(
      url(`/conversations/${encodeURIComponent(conversationId)}/human-messages/${encodeURIComponent(messageId)}/retry`),
      {},
    ),
  );
}

export function getHumanSupportUpdates(
  conversationId: string,
): Promise<{ support: HumanSupportState | null; messages: HumanSupportMessage[] }> {
  return request(() => axios.get(url(`/conversations/${encodeURIComponent(conversationId)}/support-updates`)));
}

export function markHumanSupportRead(conversationId: string, seq: number): Promise<void> {
  return request(() => axios.post(url(`/conversations/${encodeURIComponent(conversationId)}/support-read`), { seq })).then(
    () => undefined,
  );
}

export function listHumanSupportThreads(): Promise<HumanSupportThread[]> {
  return request(() => axios.get(url('/support/threads')));
}

export const HUMAN_SUPPORT_STATUS_LABEL: Record<HumanSupportTicketStatus, string> = {
  open: 'Aguardando a equipe',
  in_progress: 'Em atendimento',
  waiting_customer: 'Equipe respondeu',
  resolved: 'Encerrado',
};

const SUPPORT_SEEN_KEY = 'klinvia-support-seen-seq';
export const SUPPORT_SEEN_EVENT = 'klinvia-support-seen';

/** Último seq de resposta da equipe que o usuário viu, por conversa (aviso de nova resposta). */
export function getSupportSeenSeq(conversationId: string): number {
  try {
    const map = JSON.parse(localStorage.getItem(SUPPORT_SEEN_KEY) || '{}') as Record<string, number>;
    return Number(map[conversationId]) || 0;
  } catch {
    return 0;
  }
}

export function setSupportSeenSeq(conversationId: string, seq: number): void {
  try {
    const map = JSON.parse(localStorage.getItem(SUPPORT_SEEN_KEY) || '{}') as Record<string, number>;
    if ((Number(map[conversationId]) || 0) >= seq) return;
    map[conversationId] = seq;
    localStorage.setItem(SUPPORT_SEEN_KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent(SUPPORT_SEEN_EVENT));
  } catch {
    /* localStorage indisponível */
  }
}

export function getHelpHome(pathname: string): Promise<HelpHome> {
  return request(() => axios.get(url('/help/home'), { params: { pathname } }));
}

export function listHelpCollections(): Promise<HelpCollectionSummary[]> {
  return request(() => axios.get(url('/help/collections')));
}

export function listHelpArticles(collectionId: string): Promise<HelpArticleSummary[]> {
  return request(() => axios.get(url(`/help/collections/${encodeURIComponent(collectionId)}/articles`)));
}

export function searchHelpArticles(q: string): Promise<HelpArticleSummary[]> {
  return request(() => axios.get(url('/help/search'), { params: { q } }));
}

export function getHelpArticle(slug: string): Promise<HelpArticleDetail> {
  return request(() => axios.get(url('/help/article'), { params: { slug } }));
}

export function listHelpNews(): Promise<HelpNewsItem[]> {
  return request(() => axios.get(url('/help/news')));
}

export function getAssistantAdminMetrics(days = 30): Promise<AssistantAdminMetrics> {
  return request(() => axios.get(url('/admin/metrics'), { params: { days } }));
}

export const ASSISTANT_ASK_EVENT = 'klinvia-assistant-ask';

/** Abre a assistente global já enviando a pergunta (ex.: botão "Perguntar à IA" em mensagens de erro). */
export function askAssistant(message: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<{ message: string }>(ASSISTANT_ASK_EVENT, { detail: { message } }));
}
